import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Flex,
  Icon,
  Badge,
  Card,
  CardBody,
  useColorMode,
  Button,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  SimpleGrid,
  Checkbox,
  useToast,
  CheckboxGroup,
} from "@chakra-ui/react";
import {
  FaHeart,
  FaGraduationCap,
  FaPaintBrush,
  FaBuilding,
  FaChild,
  FaHandshake,
  FaLaptopCode,
  FaShieldAlt,
  FaCheckCircle,
  FaArrowRight,
} from "react-icons/fa";
import { useState } from "react";
import { useMutation, gql } from "@apollo/client";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../../brandConfig";

const REGISTER_FOR_EVENT = gql`
  mutation RegisterForEvent($input: RegisterForEventInput!) {
    registerForEvent(input: $input) {
      success
      message
      registration {
        id
        email
        firstName
      }
    }
  }
`;

const EVENT_ID = "conscious-economy-interest";
const EVENT_NAME = "The Conscious Economy — Expression of Interest";

const interests = [
  { id: "education", label: "Education", icon: FaGraduationCap, color: "blue" },
  { id: "art", label: "Art & Creativity", icon: FaPaintBrush, color: "pink" },
  { id: "business", label: "Business Sovereignty", icon: FaBuilding, color: "purple" },
  { id: "community", label: "Community Building", icon: FaHandshake, color: "orange" },
  { id: "technology", label: "Technology", icon: FaLaptopCode, color: "cyan" },
  { id: "coaching", label: "Coaching", icon: FaHeart, color: "red" },
  { id: "healthcare", label: "Healthcare", icon: FaShieldAlt, color: "teal" },
  { id: "parenting", label: "Parenting", icon: FaChild, color: "green" },
];

const Interest = () => {
  const { colorMode } = useColorMode();
  const toast = useToast();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);

  const [registerForEvent, { loading }] = useMutation(REGISTER_FOR_EVENT);

  const bg = getColor("background.main", colorMode);
  const cardGradientBg =
    colorMode === "light"
      ? "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,248,248,0.9) 50%, rgba(255,255,255,0.95) 100%)"
      : "linear-gradient(135deg, rgba(20,20,20,0.8) 0%, rgba(30,30,30,0.6) 50%, rgba(20,20,20,0.8) 100%)";
  const cardBorder =
    colorMode === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)";
  const textPrimary = colorMode === "light" ? "gray.800" : "white";
  const textSecondary = colorMode === "light" ? "gray.600" : "gray.300";
  const inputBg = colorMode === "light" ? "white" : "gray.800";

  const handleSubmit = async () => {
    if (!form.email || !form.email.includes("@")) {
      toast({ title: "Please enter a valid email", status: "warning", duration: 3000 });
      return;
    }
    if (!form.firstName) {
      toast({ title: "Please enter your first name", status: "warning", duration: 3000 });
      return;
    }

    const interestTags = selectedInterests.join(", ");
    const fullMessage = [
      interestTags ? `Interests: ${interestTags}` : "",
      form.message ? `Message: ${form.message}` : "",
    ].filter(Boolean).join(" | ");

    try {
      const { data } = await registerForEvent({
        variables: {
          input: {
            eventId: EVENT_ID,
            eventName: EVENT_NAME,
            email: form.email,
            firstName: form.firstName,
            lastName: form.lastName || undefined,
            source: "INTEREST_PAGE",
            utmCampaign: fullMessage || undefined,
          },
        },
      });

      if (data?.registerForEvent?.success) {
        setSubmitted(true);
        toast({
          title: "You're in!",
          description: "We'll be in touch about the Conscious Economy.",
          status: "success",
          duration: 5000,
        });
      }
    } catch (error) {
      toast({
        title: "Something went wrong. Please try again.",
        status: "error",
        duration: 3000,
      });
    }
  };

  if (submitted) {
    return (
      <Box bg={bg} minHeight="100vh">
        <NavbarWithCallToAction />
        <Container maxW="800px" py={16}>
          <VStack spacing={8} textAlign="center" py={20}>
            <Flex
              align="center"
              justify="center"
              w={20}
              h={20}
              borderRadius="full"
              bg="green.50"
            >
              <Icon as={FaCheckCircle} w={10} h={10} color="green.400" />
            </Flex>
            <Heading size="xl" color={textPrimary}>
              Welcome to the Movement
            </Heading>
            <Text fontSize="lg" color={textSecondary} maxW="500px">
              We've recorded your interest in the Conscious Economy. We'll be in touch with next steps — including upcoming webinars and how to earn your first BB Tokens.
            </Text>
            <Text fontSize="md" color={textSecondary} fontStyle="italic">
              "We don't reward capital. We reward contribution that improves the world."
            </Text>
            <Button
              as="a"
              href="/businessbuilder/journey"
              colorScheme="green"
              size="lg"
              borderRadius="full"
              rightIcon={<FaArrowRight />}
            >
              Explore the Journey
            </Button>
          </VStack>
        </Container>
        <FooterWithFourColumns />
      </Box>
    );
  }

  return (
    <Box bg={bg} minHeight="100vh">
      <NavbarWithCallToAction />

      <Container maxW="800px" py={16}>
        <VStack spacing={10} align="stretch">
          {/* Hero */}
          <Box textAlign="center" py={6}>
            <Badge
              bg="linear-gradient(135deg, #10B981 0%, #3B82F6 100%)"
              color="white"
              fontSize="md"
              px={6}
              py={2}
              borderRadius="full"
              fontWeight="600"
              mb={6}
              boxShadow="0 4px 16px rgba(16, 185, 129, 0.4)"
            >
              Expression of Interest
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: "3xl", md: "4xl" }}
              fontWeight="800"
              mb={4}
              color={textPrimary}
              bgGradient="linear(to-r, #10B981, #3B82F6)"
              bgClip="text"
            >
              Join the Conscious Economy
            </Heading>
            <Text fontSize={{ base: "md", md: "lg" }} color={textSecondary} maxW="600px" mx="auto">
              Whether you're an educator, artist, business owner, parent, or community builder — tell us what you care about and we'll show you how you can contribute.
            </Text>
          </Box>

          {/* What brings you here? */}
          <Card
            bg={cardGradientBg}
            borderWidth="1px"
            borderColor={cardBorder}
            borderRadius="2xl"
          >
            <CardBody p={8}>
              <Heading size="md" color={textPrimary} mb={2}>
                What brings you here?
              </Heading>
              <Text fontSize="sm" color={textSecondary} mb={5}>
                Select everything that resonates with you.
              </Text>

              <CheckboxGroup
                value={selectedInterests}
                onChange={(values) => setSelectedInterests(values as string[])}
              >
                <SimpleGrid columns={{ base: 2, md: 4 }} gap={3}>
                  {interests.map((interest) => (
                    <Box
                      key={interest.id}
                      bg={selectedInterests.includes(interest.id)
                        ? `${interest.color}.50`
                        : "transparent"}
                      borderWidth="1px"
                      borderColor={selectedInterests.includes(interest.id)
                        ? `${interest.color}.300`
                        : cardBorder}
                      borderRadius="xl"
                      p={3}
                      cursor="pointer"
                      transition="all 0.2s"
                      _hover={{ borderColor: `${interest.color}.300` }}
                      onClick={() => {
                        setSelectedInterests((prev) =>
                          prev.includes(interest.id)
                            ? prev.filter((i) => i !== interest.id)
                            : [...prev, interest.id]
                        );
                      }}
                    >
                      <VStack spacing={2}>
                        <Icon
                          as={interest.icon}
                          boxSize={5}
                          color={selectedInterests.includes(interest.id)
                            ? `${interest.color}.500`
                            : textSecondary}
                        />
                        <Text
                          fontSize="xs"
                          fontWeight="600"
                          color={selectedInterests.includes(interest.id)
                            ? `${interest.color}.600`
                            : textSecondary}
                          textAlign="center"
                        >
                          {interest.label}
                        </Text>
                        <Checkbox
                          value={interest.id}
                          isChecked={selectedInterests.includes(interest.id)}
                          size="sm"
                          colorScheme={interest.color}
                          onChange={() => {}} // handled by parent onClick
                          pointerEvents="none"
                        />
                      </VStack>
                    </Box>
                  ))}
                </SimpleGrid>
              </CheckboxGroup>
            </CardBody>
          </Card>

          {/* Contact Details */}
          <Card
            bg={cardGradientBg}
            borderWidth="1px"
            borderColor={cardBorder}
            borderRadius="2xl"
          >
            <CardBody p={8}>
              <Heading size="md" color={textPrimary} mb={5}>
                Your Details
              </Heading>

              <VStack spacing={4}>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="full">
                  <FormControl isRequired>
                    <FormLabel fontSize="sm" color={textSecondary}>First Name</FormLabel>
                    <Input
                      bg={inputBg}
                      borderColor={cardBorder}
                      borderRadius="lg"
                      value={form.firstName}
                      onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                      placeholder="Your first name"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" color={textSecondary}>Last Name</FormLabel>
                    <Input
                      bg={inputBg}
                      borderColor={cardBorder}
                      borderRadius="lg"
                      value={form.lastName}
                      onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                      placeholder="Your last name"
                    />
                  </FormControl>
                </SimpleGrid>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" color={textSecondary}>Email</FormLabel>
                  <Input
                    bg={inputBg}
                    borderColor={cardBorder}
                    borderRadius="lg"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="your@email.com"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" color={textSecondary}>
                    Tell us about yourself (optional)
                  </FormLabel>
                  <Textarea
                    bg={inputBg}
                    borderColor={cardBorder}
                    borderRadius="lg"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="What are you working on? What do you hope to build or change?"
                    rows={3}
                  />
                </FormControl>

                <Button
                  colorScheme="green"
                  size="lg"
                  w="full"
                  borderRadius="xl"
                  py={7}
                  fontSize="lg"
                  fontWeight="700"
                  isLoading={loading}
                  loadingText="Submitting..."
                  onClick={handleSubmit}
                  rightIcon={<FaArrowRight />}
                  boxShadow="0 4px 16px rgba(16, 185, 129, 0.3)"
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 24px rgba(16, 185, 129, 0.4)",
                  }}
                >
                  Express My Interest
                </Button>

                <Text fontSize="xs" color={textSecondary} textAlign="center">
                  No payment required. We'll send you details about upcoming webinars and how to get started.
                </Text>
              </VStack>
            </CardBody>
          </Card>

          {/* What happens next */}
          <Box textAlign="center" py={4}>
            <Heading size="sm" color={textPrimary} mb={4}>
              What Happens Next?
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
              {[
                { step: "1", title: "We reach out", desc: "We'll email you about upcoming webinars and community events" },
                { step: "2", title: "Attend a webinar", desc: "Join a free webinar and earn your first BB Tokens" },
                { step: "3", title: "Start contributing", desc: "Subscribe, complete courses, and grow with the community" },
              ].map((item) => (
                <VStack key={item.step} spacing={2}>
                  <Flex
                    w={8}
                    h={8}
                    borderRadius="full"
                    bg="green.400"
                    color="white"
                    align="center"
                    justify="center"
                    fontWeight="800"
                    fontSize="sm"
                  >
                    {item.step}
                  </Flex>
                  <Text fontSize="sm" fontWeight="700" color={textPrimary}>
                    {item.title}
                  </Text>
                  <Text fontSize="xs" color={textSecondary}>
                    {item.desc}
                  </Text>
                </VStack>
              ))}
            </SimpleGrid>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Interest;
